// Reference Libraries — effective-view lookup layer.
//
// Merges the read-only seed dataset (lib/reflibs/seed.js) with user records
// (lib/reflibs/storage.js). User IDs override seed IDs.
//
// This is the API future Kill Sheet v2 modules consume.

import { getSeedByCategory, getSeedAll } from "./seed";
import { listUserRecords } from "./storage";
import { CATEGORIES } from "./schema";

/**
 * Returns all records (seed + user) in one category, with user records
 * overriding seed records of the same id. Inactive records are filtered out.
 */
export function listByCategory(category, { includeInactive = false } = {}) {
  if (!CATEGORIES.includes(category)) return [];
  const seed = getSeedByCategory()[category] || [];
  const userAll = listUserRecords();
  const userIds = new Set(userAll.map((r) => r.id));
  const merged = [];
  // Seed first, skipping any id overridden by a user record.
  for (const s of seed) {
    if (!userIds.has(s.id)) merged.push(s);
  }
  // Then user records for this category.
  for (const u of userAll) {
    if (u.category !== category) continue;
    merged.push(u);
  }
  return includeInactive ? merged : merged.filter((r) => r.isActive !== false);
}

/** Flat list of all categories merged. */
export function listAll(opts) {
  return CATEGORIES.flatMap((c) => listByCategory(c, opts));
}

/** Find by id — checks user records first (override), then seed. */
export function findById(id) {
  if (!id) return null;
  const userAll = listUserRecords();
  const hit = userAll.find((r) => r.id === id);
  if (hit) return hit;
  return getSeedAll().find((r) => r.id === id) || null;
}

/**
 * Search/filter. All predicates optional, AND-combined. Substring matches are
 * case-insensitive. Numeric size/weight predicates accept ±0.01 tolerance.
 */
export function searchByPredicate({
  category, q, size, weight, grade, connection,
} = {}) {
  const pool = category ? listByCategory(category) : listAll();
  const qLow = q ? String(q).toLowerCase().trim() : "";
  const sizeTrim = size ? String(size).toLowerCase().trim() : "";
  const gradeLow = grade ? String(grade).toLowerCase().trim() : "";
  const connLow  = connection ? String(connection).toLowerCase().trim() : "";
  const wNum     = weight !== undefined && weight !== "" ? Number(weight) : null;

  return pool.filter((r) => {
    if (sizeTrim && !String(r.nominalSize).toLowerCase().includes(sizeTrim)) return false;
    if (gradeLow && !String(r.grade).toLowerCase().includes(gradeLow)) return false;
    if (connLow  && !String(r.connection).toLowerCase().includes(connLow))  return false;
    if (wNum != null && Number.isFinite(wNum)) {
      if (!(Math.abs(Number(r.weight) - wNum) <= 0.01)) return false;
    }
    if (qLow) {
      const hay = `${r.nominalSize} ${r.grade} ${r.connection} ${r.notes || ""}`.toLowerCase();
      if (!hay.includes(qLow)) return false;
    }
    return true;
  });
}

/** Diagnostic counts for the Hub header strip. */
export function getEffectiveCounts() {
  const out = { total: 0 };
  for (const c of CATEGORIES) {
    out[c] = listByCategory(c).length;
    out.total += out[c];
  }
  return out;
}
